/**
 * Auto-label on the inbox screens (0153): the switch's words, the one-off
 * offer's words, and the mark on a label Auto-label put on a thread.
 *
 * The wire types live here rather than in the shared web types, because the
 * inbox is the only screen that reads them.
 *
 * NO MODEL IS NAMED. Every sentence a person reads says Auto-label and never
 * the model, the vendor or the router behind it.
 */
import type { ConversationLabel } from '../../lib/types'

/** `GET /inbox/autolabel`, and the answer to `PUT` on it. */
export interface AutoLabelState {
  enabled: boolean
  /** Whether this server can run Auto-label at all. False means the switch cannot be turned on. */
  available: boolean
  updatedAt: string | null
  /** The most open threads one press of Label open threads now takes. */
  openThreadsMax: number
}

/** What one press of Label open threads now did. */
export interface AutoLabelOpenThreadsResult {
  queued: number
  notQueued: number
  limit: number
  more: boolean
}

/** The switch's name, the reference's word. */
export const AUTOLABEL_NAME = 'Auto-label'

/** The mark's tooltip on a label Auto-label put on a thread. */
export const AUTOLABEL_APPLIED_TITLE = 'Applied by Auto-label'

export const AUTOLABEL_UNAVAILABLE_HINT = 'Auto-label is not set up on this server yet.'

/** What the switch's tooltip says, for the state it is in. Undefined while it loads. */
export function autoLabelSwitchHint(state: AutoLabelState | undefined): string {
  if (state === undefined) return 'Auto-label puts your labels on new replies by itself.'
  if (!state.available) return AUTOLABEL_UNAVAILABLE_HINT
  return state.enabled
    ? 'On. New replies get your labels by themselves. A label you take off stays off.'
    : 'Off. Turn it on to put your labels on new replies by themselves.'
}

/**
 * Who put a label on a thread, as the thread payload says it.
 *
 * Read off the label rather than declared on the shared type, which is not
 * this screen's to change. Anything but the one word reads as a person's,
 * which is what every label put on before Auto-label existed is.
 */
export function appliedByAutoLabel(label: ConversationLabel): boolean {
  return (label as ConversationLabel & { appliedBy?: unknown }).appliedBy === 'autolabel'
}

/** The offer shown once, the moment the switch goes on. */
export function autoLabelOfferCopy(openThreadsMax: number | undefined): { title: string; description: string; body: string } {
  const bound =
    openThreadsMax === undefined ? 'the open threads with a reply' : `up to ${openThreadsMax} open threads with a reply`
  return {
    title: 'Auto-label is on',
    description: 'New replies get your labels by themselves as they arrive.',
    body: `It can also read the threads already in the inbox now, ${bound}, newest first. It only adds labels, and a label you take off stays off.`,
  }
}

/** What a press of Label open threads now came to, as a toast says it. */
export function openThreadsToast(result: AutoLabelOpenThreadsResult): {
  tone: 'success' | 'warning' | 'danger' | 'neutral'
  title: string
  description?: string
} {
  const total = result.queued + result.notQueued
  const threads = (n: number) => `${n} open thread${n === 1 ? '' : 's'}`
  if (total === 0) return { tone: 'neutral', title: 'No open threads to label' }
  if (result.queued === 0) {
    return { tone: 'danger', title: 'The open threads could not be queued', description: 'Nothing was labelled. Try again in a minute.' }
  }
  if (result.notQueued > 0) {
    return {
      tone: 'warning',
      title: `Labelling ${result.queued} of ${threads(total)}`,
      description: `${result.notQueued} could not be queued. Press again in a minute for those.`,
    }
  }
  return {
    tone: 'success',
    title: `Labelling ${threads(result.queued)}`,
    description: result.more
      ? `The newest ${result.limit}. Older threads are left as they are. Labels appear as each thread is read.`
      : 'Labels appear as each thread is read.',
  }
}
